import { lazy, Suspense } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { RoleProvider } from "@/contexts/RoleContext";
import { LearningProvider } from "@/contexts/LearningContext";
import { GranularityProvider } from "@/contexts/GranularityContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { DataSourceProvider } from "@/contexts/DataSourceContext";
import { OrganizationSettingsProvider } from "@/contexts/OrganizationSettingsContext";
const ProtectedRoute = lazy(() => import("@/components/auth/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));
const AppLayout = lazy(() => import("@/components/layout/AppLayout").then(m => ({ default: m.AppLayout })));
const MetricLearningDrawer = lazy(() => import("@/components/learning/MetricLearningDrawer").then(m => ({ default: m.MetricLearningDrawer })));

// All pages lazy loaded
const LandingPage = lazy(() => import("./pages/LandingPage"));

// Auth Pages - lazy loaded
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./pages/auth/SignupPage"));

// Lazy-loaded auth pages
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const OnboardingPage = lazy(() => import("./pages/auth/OnboardingPage"));
const AcceptInvitationPage = lazy(() => import("./pages/auth/AcceptInvitationPage"));
const SubscriptionRequiredPage = lazy(() => import("./pages/auth/SubscriptionRequiredPage"));
const SelectOrganizationPage = lazy(() => import("./pages/SelectOrganizationPage"));

// Lazy-loaded App Pages
const SalesAnalytics = lazy(() => import("./pages/SalesAnalytics"));
const RevenueDashboard = lazy(() => import("./pages/RevenueDashboard"));
const FunnelAnalysis = lazy(() => import("./pages/FunnelAnalysis"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const DealsPage = lazy(() => import("./pages/DealsPage"));
const HygieneDashboard = lazy(() => import("./pages/HygieneDashboard"));
const FollowUpsPage = lazy(() => import("./pages/FollowUpsPage"));
const AtRiskPage = lazy(() => import("./pages/AtRiskPage"));
const ForecastPage = lazy(() => import("./pages/ForecastPage"));
const FunnelBuilderPage = lazy(() => import("./pages/FunnelBuilderPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TargetsPage = lazy(() => import("./pages/TargetsPage"));
const DataExportPage = lazy(() => import("./pages/DataExportPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const BIDashboardsPage = lazy(() => import("./pages/BIDashboardsPage"));
const CRMAdvisorPage = lazy(() => import("./pages/CRMAdvisorPage"));
const PipelineCoveragePage = lazy(() => import("./pages/PipelineCoveragePage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const AIAdvisorPage = lazy(() => import("./pages/AIAdvisorPage"));
const ICPStudioPage = lazy(() => import("./pages/ICPStudioPage"));
const ICPDetailPage = lazy(() => import("./pages/ICPDetailPage"));
const ICPBuilderPage = lazy(() => import("./pages/ICPBuilderPage"));
const PlaybooksPage = lazy(() => import("./pages/PlaybooksPage"));
const LearnPage = lazy(() => import("./pages/LearnPage"));
const SimulatorPage = lazy(() => import("./pages/SimulatorPage"));
const FunnelDrilldownPage = lazy(() => import("./pages/FunnelDrilldownPage"));
const SalesBurndownPage = lazy(() => import("./pages/SalesBurndownPage"));
const ExecutiveOverviewPage = lazy(() => import("./pages/ExecutiveOverviewPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));

// Marketing Pages
const FeaturesPage = lazy(() => import("./pages/marketing/FeaturesPage"));
const IntegrationsMarketingPage = lazy(() => import("./pages/marketing/IntegrationsMarketingPage"));
const AboutPage = lazy(() => import("./pages/marketing/AboutPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/marketing/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/marketing/TermsOfServicePage"));
const DocumentationPage = lazy(() => import("./pages/marketing/DocumentationPage"));
const HelpCenterPage = lazy(() => import("./pages/marketing/HelpCenterPage"));
const RevOpsGuidePage = lazy(() => import("./pages/marketing/RevOpsGuidePage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const DocumentationArticlePage = lazy(() => import("./pages/marketing/DocumentationArticlePage"));
const NPSResponsePage = lazy(() => import("./pages/NPSResponsePage"));

// Settings Pages
const BillingPage = lazy(() => import("./pages/settings/BillingPage"));
const PermissionProfilesPage = lazy(() => import("./pages/settings/PermissionProfilesPage"));
const UsersManagementPage = lazy(() => import("./pages/settings/UsersManagementPage"));
const SlackConfigPage = lazy(() => import("./pages/settings/SlackConfigPage"));

// Admin Pages
const AdminPanel = lazy(() => import("./pages/admin/AdminPanel"));

// Preview Pages
const PreviewExecutiveOverview = lazy(() => import("./pages/preview/PreviewExecutiveOverview"));
const PreviewForecast = lazy(() => import("./pages/preview/PreviewForecast"));
const PreviewAIAdvisor = lazy(() => import("./pages/preview/PreviewAIAdvisor"));
const PreviewPlaybooks = lazy(() => import("./pages/preview/PreviewPlaybooks"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 min default — avoid refetching on every mount/focus
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <ErrorBoundary>
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <GranularityProvider>
          <BrowserRouter>
            <AuthProvider>
              <OrganizationSettingsProvider>
              <DataSourceProvider>
              <RoleProvider>
                <LearningProvider>
                  <Toaster />
                  <Sonner />
                  <Suspense fallback={<div className="min-h-screen" />}>
                  <MetricLearningDrawer />
                  <Routes>
                    {/* Public */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/landing" element={<Navigate to="/" replace />} />
                    <Route path="/preview/executive-overview" element={<PreviewExecutiveOverview />} />
                    <Route path="/preview/forecast" element={<PreviewForecast />} />
                    <Route path="/preview/ai-advisor" element={<PreviewAIAdvisor />} />
                    <Route path="/preview/playbooks" element={<PreviewPlaybooks />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/demo" element={<DemoPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/integrations" element={<IntegrationsMarketingPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsOfServicePage />} />
                    <Route path="/documentation" element={<DocumentationPage />} />
                    <Route path="/documentation/:slug" element={<DocumentationArticlePage />} />
                    <Route path="/help" element={<HelpCenterPage />} />
                    <Route path="/revops-guide" element={<RevOpsGuidePage />} />
                    <Route path="/blog" element={<BlogListPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/nps-response" element={<NPSResponsePage />} />
                    
                    {/* Auth */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                    
                    {/* Org Selection (multi-org) */}
                    <Route path="/select-organization" element={
                      <ProtectedRoute requireOrganization={false} requireSubscription={false}>
                        <SelectOrganizationPage />
                      </ProtectedRoute>
                    } />
                    
                    {/* Onboarding */}
                    <Route path="/onboarding" element={
                      <ProtectedRoute requireOrganization={false} requireSubscription={false}>
                        <OnboardingPage />
                      </ProtectedRoute>
                    } />
                    
                    {/* Subscription Required */}
                    <Route path="/subscription-required" element={
                      <ProtectedRoute requireOrganization={true} requireSubscription={false}>
                        <SubscriptionRequiredPage />
                      </ProtectedRoute>
                    } />
                    
                    {/* Protected App Routes */}
                    <Route element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }>
                      <Route path="/app" element={<Navigate to="/executive-overview" replace />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                    
                      {/* Operational */}
                      <Route path="/leads" element={<LeadsPage />} />
                      <Route path="/deals" element={<DealsPage />} />
                      <Route path="/follow-ups" element={<FollowUpsPage />} />
                      <Route path="/at-risk" element={<AtRiskPage />} />
                      
                      {/* Tactical */}
                      <Route path="/funnel" element={<FunnelAnalysis />} />
                      <Route path="/pipeline" element={<ProtectedRoute requiredPermission="pipeline"><PipelineCoveragePage /></ProtectedRoute>} />
                      
                      {/* Executive */}
                      <Route path="/executive-overview" element={<ProtectedRoute requiredPermission="executive_overview"><ExecutiveOverviewPage /></ProtectedRoute>} />
                      <Route path="/revenue" element={<ProtectedRoute requiredPermission="revenue"><RevenueDashboard /></ProtectedRoute>} />
                      <Route path="/burndown" element={<ProtectedRoute requiredPermission="burndown"><SalesBurndownPage /></ProtectedRoute>} />
                      <Route path="/forecast" element={<ProtectedRoute requiredPermission="forecast"><ForecastPage /></ProtectedRoute>} />
                      <Route path="/targets" element={<ProtectedRoute requiredPermission="targets"><TargetsPage /></ProtectedRoute>} />
                      
                      {/* Playbooks */}
                      <Route path="/playbooks" element={<ProtectedRoute requiredPermission="playbooks"><PlaybooksPage /></ProtectedRoute>} />
                      
                      {/* Simulator */}
                      <Route path="/simulator" element={<ProtectedRoute requiredPermission="simulator"><SimulatorPage /></ProtectedRoute>} />
                      
                      {/* Reports */}
                      <Route path="/reports" element={<ProtectedRoute requiredPermission="reports"><ReportsPage /></ProtectedRoute>} />
                      
                      {/* Analytics */}
                      <Route path="/analytics" element={<ProtectedRoute requiredPermission="analytics"><SalesAnalytics /></ProtectedRoute>} />
                      <Route path="/icp-studio" element={<ProtectedRoute requiredPermission="icp_studio"><ICPStudioPage /></ProtectedRoute>} />
                      <Route path="/icp-studio/new" element={<ProtectedRoute requiredPermission="icp_studio"><ICPBuilderPage /></ProtectedRoute>} />
                      <Route path="/icp-studio/:icpId" element={<ProtectedRoute requiredPermission="icp_studio"><ICPDetailPage /></ProtectedRoute>} />
                      <Route path="/icp-studio/:icpId/edit" element={<ProtectedRoute requiredPermission="icp_studio"><ICPBuilderPage /></ProtectedRoute>} />
                      <Route path="/icp" element={<Navigate to="/icp-studio" replace />} />
                      <Route path="/sales-execution" element={<Navigate to="/analytics" replace />} />
                      <Route path="/rep-performance" element={<Navigate to="/analytics" replace />} />
                      <Route path="/cohorts" element={<Navigate to="/analytics" replace />} />
                      <Route path="/hygiene" element={<ProtectedRoute requiredPermission="hygiene"><HygieneDashboard /></ProtectedRoute>} />
                      <Route path="/export" element={<ProtectedRoute requiredPermission="data_export"><DataExportPage /></ProtectedRoute>} />
                      <Route path="/dashboards" element={<ProtectedRoute requiredPermission="dashboards"><BIDashboardsPage /></ProtectedRoute>} />
                      
                      {/* Learning */}
                      <Route path="/learn" element={<ProtectedRoute requiredPermission="learning"><LearnPage /></ProtectedRoute>} />
                      
                      {/* Settings */}
                      <Route path="/settings" element={<ProtectedRoute requiredPermission="settings"><SettingsPage /></ProtectedRoute>} />
                      <Route path="/settings/funnels" element={<ProtectedRoute requiredPermission="funnel_builder"><FunnelBuilderPage /></ProtectedRoute>} />
                      <Route path="/settings/products" element={<ProtectedRoute requiredPermission="products"><ProductsPage /></ProtectedRoute>} />
                      <Route path="/settings/integrations" element={<ProtectedRoute requiredPermission="integrations"><IntegrationsPage /></ProtectedRoute>} />
                      <Route path="/settings/billing" element={<ProtectedRoute requiredPermission="billing"><BillingPage /></ProtectedRoute>} />
                      <Route path="/settings/users" element={<ProtectedRoute requiredPermission="user_management"><UsersManagementPage /></ProtectedRoute>} />
                      <Route path="/settings/permissions" element={<ProtectedRoute requiredPermission="permission_management"><PermissionProfilesPage /></ProtectedRoute>} />
                      <Route path="/settings/slack" element={<SlackConfigPage />} />
                      <Route path="/crm-advisor" element={<ProtectedRoute requiredPermission="crm_advisor"><CRMAdvisorPage /></ProtectedRoute>} />
                      <Route path="/ai-advisor" element={<ProtectedRoute requiredPermission="ai_advisor"><AIAdvisorPage /></ProtectedRoute>} />
                      
                      {/* Drilldown */}
                      <Route path="/funnel-drilldown" element={<FunnelDrilldownPage />} />
                      
                      {/* Admin */}
                      <Route path="/admin" element={<AdminPanel />} />
                    </Route>
                    
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </LearningProvider>
              </RoleProvider>
              </DataSourceProvider>
              </OrganizationSettingsProvider>
            </AuthProvider>
          </BrowserRouter>
        </GranularityProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  </ErrorBoundary>
);

export default App;
